<template>
  <div class="lf">
    <div v-if="modelValue.length || suggestions.length" class="lf__chips">
      <span v-for="link in modelValue" :key="link.type + link.referenceId" class="lf__chip">
        <span class="lf__type">{{ typeLabel(link.type) }}</span>
        <span class="lf__label">{{ link.label || link.referenceId }}</span>
        <button type="button" class="lf__x" :aria-label="t('calendar.links.remove', { name: link.label })" @click="remove(link)">×</button>
      </span>
      <span v-for="link in suggestions" :key="'s' + link.type + link.referenceId" class="lf__chip lf__chip--suggest">
        <span class="lf__type">{{ typeLabel(link.type) }}</span>
        <span class="lf__label">{{ link.label || link.referenceId }}</span>
        <button type="button" class="lf__ok" :title="t('calendar.links.accept')" @click="accept(link)">✓</button>
        <button type="button" class="lf__x" :title="t('calendar.links.dismiss')" @click="$emit('dismiss', link)">×</button>
      </span>
    </div>
    <div class="lf__pick">
      <UiSelect v-model="type" class="lf__type-select" :options="typeOptions" :placeholder="t('calendar.links.type')" />
      <UiReferenceField
        v-if="type"
        :key="type"
        v-model="label"
        v-model:reference-id="referenceId"
        class="lf__field"
        :type="type"
        :placeholder="t('calendar.links.search')"
        @picked="pick"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiReferenceField, UiSelect } from '@general/ui-kit'

import { calendarApi } from '../api'

/**
 * The records of the ERP an event is about: a supplier, an order, a work, a
 * customer — any type `platform.references` publishes for this person, so a
 * module installed next year appears here without a line changing.
 */
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  suggestions: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:modelValue', 'dismiss'])
const { t, te } = useI18n()

const types = ref([])
const type = ref('')
const label = ref('')
const referenceId = ref(null)

onMounted(async () => {
  try { types.value = (await calendarApi.referenceTypes()).types ?? [] } catch { types.value = [] }
})

function typeLabel(value) {
  const key = types.value.find(entry => entry.type === value)?.label
  if (key && te(key)) return t(key)
  const own = `calendar.types.${value}`
  return te(own) ? t(own) : value
}
const typeOptions = computed(() => types.value
  .filter(entry => entry.type !== 'calendar_event')
  .map(entry => ({ value: entry.type, label: typeLabel(entry.type) }))
  .sort((a, b) => a.label.localeCompare(b.label)))

const same = (a, b) => a.type === b.type && String(a.referenceId) === String(b.referenceId)
function pick(item) {
  if (!item?.id) return
  const link = { type: type.value, referenceId: String(item.id), label: item.label }
  if (!props.modelValue.some(entry => same(entry, link))) emit('update:modelValue', [...props.modelValue, link])
  label.value = ''
  referenceId.value = null
}
function accept(link) {
  if (!props.modelValue.some(entry => same(entry, link))) emit('update:modelValue', [...props.modelValue, { type: link.type, referenceId: link.referenceId, label: link.label }])
  emit('dismiss', link)
}
function remove(link) {
  emit('update:modelValue', props.modelValue.filter(entry => !same(entry, link)))
}
</script>

<style scoped>
.lf { display: flex; flex-direction: column; gap: 8px; }
.lf__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.lf__chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px 3px 10px; border-radius: var(--radius-full); background: var(--color-surface-2); border: 1px solid var(--color-border); font-size: var(--text-sm); max-width: 100%; }
.lf__chip--suggest { border-style: dashed; border-color: var(--color-primary); }
.lf__type { color: var(--color-text-soft); font-size: var(--text-xs); }
.lf__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lf__x, .lf__ok { border: 0; background: none; cursor: pointer; font-size: 15px; line-height: 1; width: 26px; height: 26px; border-radius: 50%; color: var(--color-text-soft); }
.lf__ok { color: var(--color-success); }
.lf__x:hover, .lf__ok:hover { background: var(--color-gray-100); }
.lf__pick { display: flex; flex-wrap: wrap; gap: 8px; }
.lf__type-select { min-width: 170px; flex: 0 1 200px; }
.lf__field { flex: 1 1 220px; min-width: 0; }
/* Con el dedo, la cruz y el visto de 26 px se fallaban: se tocaba la ficha de
   al lado. El buscador ya se porta en el teléfono (UiReferenceField). */
@media (pointer: coarse) {
  .lf__x, .lf__ok { width: 36px; height: 36px; }
  .lf__field { flex-basis: 100%; }
}
</style>
